/**
 * Back In Stock Drawer Component
 * Zara-inspired drawer for displaying size availability and email notification signup
 */

if (!customElements.get('back-in-stock-drawer')) {
  class BackInStockDrawer extends HTMLElement {
    constructor() {
      super();

      // Element references
      this.overlay = this.querySelector('[data-bis-overlay]');
      this.closeButton = this.querySelector('[data-bis-close]');
      this.lengthContainer = this.querySelector('[data-length-container]');
      this.lengthList = this.querySelector('[data-length-list]');
      this.sizesContainer = this.querySelector('[data-sizes-container]');
      this.sizesList = this.querySelector('[data-sizes-list]');
      this.formContainer = this.querySelector('[data-form-container]');
      this.form = this.querySelector('[data-bis-form]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.phoneInput = this.querySelector('[data-bis-phone]');
      this.methodTabs = this.querySelectorAll('[data-bis-method]');
      this.methodFields = this.querySelectorAll('[data-bis-field]');
      this.notifyMethod = 'email';
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.submitSpinner = this.submitButton?.querySelector('.loading__spinner');
      this.backButton = this.querySelector('[data-back-to-sizes]');
      this.backToLengthButton = this.querySelector('[data-back-to-length]');
      this.selectedSizeDisplay = this.querySelector('[data-selected-size]');
      this.successMessage = this.querySelector('[data-success-message]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.errorText = this.querySelector('[data-error-text]');
      this.subheading = this.querySelector('[data-bis-subheading]');
      this.originalSubheadingText = this.subheading?.textContent?.trim() || '';

      // State
      this.activeElement = null;
      this.selectedVariantId = null;
      this.selectedSizeValue = null;
      this.selectedVariantSku = null;
      this.selectedVariantPrice = null;
      this.selectedLength = null;
      this.isSubmitting = false;

      // Product data from data attributes
      this.productId = this.dataset.productId;
      this.productTitle = this.dataset.productTitle;
      this.productUrl = this.dataset.productUrl;
      this.productImage = this.dataset.productImage;
      this.sizeOptionPosition = parseInt(this.dataset.sizeOptionPosition) || 1;
      this.lengthOptionPosition = parseInt(this.dataset.lengthOptionPosition) || null;
      this.colorOptionPosition = parseInt(this.dataset.colorOptionPosition) || null;
      this.currentColor = this.dataset.currentColor || null;
      this.currentLength = this.dataset.currentLength || null;
      this.hasLengthOption = !!this.lengthContainer;

      // Parse variants data
      const variantsScript = this.querySelector('[data-bis-variants]');
      this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

      // Bind methods
      this.handleKeyUp = this.handleKeyUp.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleLengthClick = this.handleLengthClick.bind(this);
      this.handleSizeClick = this.handleSizeClick.bind(this);
      this.handleBackClick = this.handleBackClick.bind(this);
      this.handleBackToLengthClick = this.handleBackToLengthClick.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);

      // Initialize events
      this.bindEvents();

      // Subscribe to variant change events
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    bindEvents() {
      this.addEventListener('keyup', this.handleKeyUp);
      this.overlay?.addEventListener('click', this.handleOverlayClick);
      this.closeButton?.addEventListener('click', () => this.close());
      this.backButton?.addEventListener('click', this.handleBackClick);
      this.backToLengthButton?.addEventListener('click', this.handleBackToLengthClick);
      this.form?.addEventListener('submit', this.handleFormSubmit);

      this.methodTabs?.forEach((tab) => {
        tab.addEventListener('click', () => this.setNotifyMethod(tab.dataset.bisMethod));
      });

      // Delegate length button clicks
      this.lengthList?.addEventListener('click', this.handleLengthClick);

      // Delegate size button clicks
      this.sizesList?.addEventListener('click', this.handleSizeClick);
    }

    // ----- OPEN/CLOSE METHODS -----

    open(triggeredBy = null) {
      if (triggeredBy) {
        this.activeElement = triggeredBy;
      }

      // Store original parent so we can restore on close
      if (!this._originalParent) {
        this._originalParent = this.parentElement;
        this._originalNextSibling = this.nextElementSibling;
      }

      // Move drawer to body to escape parent stacking contexts
      document.body.appendChild(this);

      // Reset to initial state
      this.resetToSizeSelection();

      // Update sizes based on current color selection
      this.updateSizesForCurrentColor();

      // Pre-select the current size from PDP
      this.preselectCurrentSize();

      // Animate in
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      // Trap focus after transition
      this.addEventListener('transitionend', () => {
        if (!this.classList.contains('active')) return;
        const focusTarget = this.querySelector('.bis-drawer__inner') || this.closeButton;
        if (typeof trapFocus === 'function') {
          trapFocus(this, focusTarget);
        }
      }, { once: true });

      // Prevent body scroll and signal drawer is open (for hiding sticky ATC)
      document.body.classList.add('overflow-hidden', 'bis-drawer-open');

      // Dispatch open event
      this.dispatchEvent(new CustomEvent('bis-drawer:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('active');

      // Restore body scroll and remove drawer open signal
      document.body.classList.remove('overflow-hidden', 'bis-drawer-open');
      document.documentElement.classList.remove('overflow-hidden');

      // Release focus trap
      if (typeof removeTrapFocus === 'function') {
        try {
          removeTrapFocus(this.activeElement);
        } catch (e) {
          // ignore
        }
      }

      // Dispatch close event
      this.dispatchEvent(new CustomEvent('bis-drawer:close', { bubbles: true }));
    }

    // ----- EVENT HANDLERS -----

    handleKeyUp(event) {
      if (event.code === 'Escape') {
        this.close();
      }
    }

    handleOverlayClick() {
      this.close();
    }

    handleSizeClick(event) {
      const button = event.target.closest('.bis-drawer__size-button');
      if (!button) return;

      const sizeItem = button.closest('.bis-drawer__size-item');
      const variantId = sizeItem.dataset.variantId;
      const sizeValue = sizeItem.dataset.sizeValue;
      const variantSku = sizeItem.dataset.variantSku || '';
      const variantPrice = sizeItem.dataset.variantPrice || '0';
      const isAvailable = sizeItem.dataset.available === 'true';
      const action = button.dataset.action;

      // Update visual selection
      this.updateSelectedSize(sizeItem);

      if (action === 'select-size' && isAvailable) {
        // Available size - close drawer and select variant
        this.selectAvailableVariant(variantId);
      } else if (action === 'notify-me' && !isAvailable) {
        // Unavailable size - show email form
        this.showEmailForm(variantId, sizeValue, variantSku, variantPrice);
      }
    }

    handleLengthClick(event) {
      const button = event.target.closest('.bis-drawer__size-button');
      if (!button || button.dataset.action !== 'select-length') return;

      const lengthItem = button.closest('.bis-drawer__size-item');
      const lengthValue = lengthItem.dataset.lengthValue;

      // Update visual selection on length list
      const lengthItems = this.lengthList?.querySelectorAll('.bis-drawer__size-item');
      lengthItems?.forEach(item => item.classList.remove('bis-drawer__size-item--selected'));
      lengthItem.classList.add('bis-drawer__size-item--selected');

      // Store selected length
      this.selectedLength = lengthValue;

      // Hide length step, show sizes step
      this.lengthContainer?.setAttribute('hidden', '');
      this.sizesContainer?.removeAttribute('hidden');

      // Update sizes filtered by colour + length
      this.updateSizesForCurrentSelection();
    }

    handleBackToLengthClick() {
      // Hide sizes, show length
      this.sizesContainer?.setAttribute('hidden', '');
      this.lengthContainer?.removeAttribute('hidden');
      this.selectedLength = null;
    }

    handleBackClick() {
      this.resetToSizeSelection();
    }

    async handleFormSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      if (!this.selectedVariantId) {
        this.showError('No variant selected. Please try again.');
        return;
      }

      if (this.notifyMethod === 'sms') {
        const phoneNumber = this.normalisePhoneNumber(this.phoneInput?.value);

        if (!phoneNumber) {
          this.showError('Please enter a valid mobile number.');
          return;
        }

        await this.subscribeToBackInStock({ phoneNumber });
        return;
      }

      const email = this.emailInput?.value?.trim();

      if (!email || !this.validateEmail(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      await this.subscribeToBackInStock({ email });
    }

    handleVariantChange(event) {
      const data = event?.data || event;
      if (!data) return;

      // Update current color if color option changed
      if (this.colorOptionPosition && data.variant) {
        const newColor = data.variant.options[this.colorOptionPosition - 1];
        if (newColor && newColor !== this.currentColor) {
          this.currentColor = newColor;
          this.dataset.currentColor = newColor;

          // If drawer is open, update sizes
          if (this.classList.contains('active')) {
            this.updateSizesForCurrentColor();
          }
        }
      }
    }

    // ----- UI STATE METHODS -----

    resetToSizeSelection() {
      // Restore original header text
      if (this.subheading && this.originalSubheadingText) {
        this.subheading.textContent = this.originalSubheadingText;
      }

      // Hide form
      this.formContainer?.setAttribute('hidden', '');

      // If product has length option, go back to length step
      if (this.hasLengthOption) {
        this.sizesContainer?.setAttribute('hidden', '');
        this.lengthContainer?.removeAttribute('hidden');
        this.selectedLength = null;
      } else {
        this.sizesContainer?.removeAttribute('hidden');
      }

      // Reset form state
      this.successMessage?.setAttribute('hidden', '');
      this.errorMessage?.setAttribute('hidden', '');
      this.form?.removeAttribute('hidden');
      this.form?.reset();
      // form.reset() restores values, not which method is showing.
      this.setNotifyMethod('email', { focus: false });

      // Clear selection
      this.selectedVariantId = null;
      this.selectedSizeValue = null;
      this.selectedVariantSku = null;
      this.selectedVariantPrice = null;

      // Reset submit button
      this.submitButton?.classList.remove('loading');
      this.submitButton?.removeAttribute('disabled');
      this.submitSpinner?.classList.add('hidden');
      this.isSubmitting = false;
    }

    showEmailForm(variantId, sizeValue, variantSku, variantPrice) {
      this.selectedVariantId = variantId;
      this.selectedSizeValue = sizeValue;
      this.selectedVariantSku = variantSku;
      this.selectedVariantPrice = variantPrice;

      // Update display
      if (this.selectedSizeDisplay) {
        this.selectedSizeDisplay.textContent = sizeValue;
      }

      // Update header text for form step
      if (this.subheading) {
        this.subheading.textContent = "We'll notify you when it's back in stock";
      }

      // Hide sizes, show form
      this.sizesContainer?.setAttribute('hidden', '');
      this.formContainer?.removeAttribute('hidden');

      // Clear any previous errors
      this.errorMessage?.setAttribute('hidden', '');

      // Focus email input
      requestAnimationFrame(() => {
        this.emailInput?.focus();
      });

      // Pre-fill email if customer is logged in
      this.prefillCustomerEmail();
    }

    showSuccess() {
      this.form?.setAttribute('hidden', '');
      this.errorMessage?.setAttribute('hidden', '');
      this.successMessage?.removeAttribute('hidden');

      // Update subheading for success state
      if (this.subheading) {
        this.subheading.textContent = "We'll let you know when it's available";
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        this.close();
      }, 3000);
    }

    showError(message) {
      if (this.errorText) {
        this.errorText.textContent = message;
      }
      this.errorMessage?.removeAttribute('hidden');

      // Hide after delay
      setTimeout(() => {
        this.errorMessage?.setAttribute('hidden', '');
      }, 5000);
    }

    updateSizesForCurrentColor() {
      this.updateSizesForCurrentSelection();
    }

    updateSizesForCurrentSelection() {
      if (!this.sizesList) return;

      const sizeItems = this.sizesList.querySelectorAll('.bis-drawer__size-item');
      const activeLength = this.selectedLength || this.currentLength;

      sizeItems.forEach(item => {
        const sizeValue = item.dataset.sizeValue;

        // Find variant matching this size + current colour + selected length
        let matchingVariant = null;

        for (const variant of this.variants) {
          const variantSize = variant.options[this.sizeOptionPosition - 1];
          if (variantSize !== sizeValue) continue;

          // Check colour match
          if (this.colorOptionPosition && this.currentColor) {
            const variantColor = variant.options[this.colorOptionPosition - 1];
            if (variantColor !== this.currentColor) continue;
          }

          // Check length match
          if (this.lengthOptionPosition && activeLength) {
            const variantLength = variant.options[this.lengthOptionPosition - 1];
            if (variantLength !== activeLength) continue;
          }

          matchingVariant = variant;
          break;
        }

        if (matchingVariant) {
          const isAvailable = matchingVariant.available;

          // Update data attributes
          item.dataset.variantId = matchingVariant.id;
          item.dataset.available = String(isAvailable);
          item.dataset.variantSku = matchingVariant.sku || '';
          item.dataset.variantPrice = matchingVariant.price;

          // Update classes
          item.classList.toggle('bis-drawer__size-item--unavailable', !isAvailable);

          // Update button action
          const button = item.querySelector('.bis-drawer__size-button');
          if (button) {
            button.dataset.action = isAvailable ? 'select-size' : 'notify-me';
          }

          // Update status text
          const statusEl = item.querySelector('.bis-drawer__size-status');
          if (statusEl) {
            statusEl.textContent = isAvailable ? 'In Stock' : 'Notify Me';
          }

          // Show the item
          item.style.display = '';
        } else {
          // No variant for this combination - hide it
          item.style.display = 'none';
        }
      });
    }

    preselectCurrentSize() {
      // Get the currently selected size from the PDP variant picker
      const productInfo = document.querySelector('product-info');
      if (!productInfo) return;

      // Try to find the selected size input
      const sizeInputs = productInfo.querySelectorAll('input[type="radio"]:checked');
      let currentSize = null;

      for (const input of sizeInputs) {
        // Check if this is a size option by looking at the fieldset or parent
        const fieldset = input.closest('fieldset');
        const legend = fieldset?.querySelector('legend, .form__label');
        const legendText = legend?.textContent?.toLowerCase() || '';

        if (legendText.includes('size') || legendText.includes('taille')) {
          currentSize = input.value;
          break;
        }
      }

      // Also try to get size from the variant-radios data
      if (!currentSize) {
        const variantRadios = productInfo.querySelector('variant-radios, variant-selects');
        if (variantRadios) {
          const sizeFieldset = variantRadios.querySelector('[data-option-index="1"], fieldset:nth-child(2)');
          const checkedInput = sizeFieldset?.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            currentSize = checkedInput.value;
          }
        }
      }

      // Clear all selected states first
      const sizeItems = this.sizesList?.querySelectorAll('.bis-drawer__size-item');
      sizeItems?.forEach(item => {
        item.classList.remove('bis-drawer__size-item--selected');
      });

      // If we found a current size, select it
      if (currentSize && this.sizesList) {
        const matchingItem = this.sizesList.querySelector(`[data-size-value="${currentSize}"]`);
        if (matchingItem) {
          matchingItem.classList.add('bis-drawer__size-item--selected');
        }
      }
    }

    updateSelectedSize(sizeItem) {
      // Clear all selected states
      const sizeItems = this.sizesList?.querySelectorAll('.bis-drawer__size-item');
      sizeItems?.forEach(item => {
        item.classList.remove('bis-drawer__size-item--selected');
      });

      // Add selected to clicked item
      sizeItem.classList.add('bis-drawer__size-item--selected');
    }

    // ----- BACK IN STOCK API INTEGRATION -----
    //
    // Provider: Back in Stock by AMP (window.BIS), which replaces Swym's
    // back-in-stock alerts. Swym itself stays installed for the wishlist, so
    // `_swat` may still be on the page — it is deliberately not used here.
    // Docs: https://help.useamp.com/article/757-javascript-api

    async subscribeToBackInStock({ email = null, phoneNumber = null } = {}) {
      this.isSubmitting = true;
      this.submitButton?.classList.add('loading');
      this.submitButton?.setAttribute('disabled', 'disabled');
      this.submitSpinner?.classList.remove('hidden');
      this.errorMessage?.setAttribute('hidden', '');

      try {
        await this.ensureBackInStockLoaded();

        if (!window.BIS || typeof window.BIS.create !== 'function') {
          // Inherited from Swym, this used to blame the cookie banner. It is
          // almost always the provider simply not being on the page — the app
          // embed off for this theme, or the script still loading — so say
          // something true and leave the diagnosis to the console line below.
          console.error('[BIS Drawer] window.BIS is unavailable — is the Back in Stock app embed enabled for this theme?');
          throw new Error('Stock notifications are unavailable right now. Please refresh and try again.');
        }

        const variantId = parseInt(this.selectedVariantId, 10);
        const productId = parseInt(this.productId, 10);

        // BIS.create returns AMP's own thenable, not a native promise: it has
        // then() but no catch(), and a failed request still arrives through
        // then() — distinguished only by data.status. Wrap it so the rest of
        // this method can await normally and race the timeout below.
        //
        // No `accepts_marketing` is passed: the form's consent line only covers
        // the privacy policy, so opting these addresses into marketing would be
        // consent we never asked for. Add a checkbox first if that changes.
        const request = new Promise((resolve, reject) => {
          try {
            // Email and SMS are the same call: pass null for the address and
            // the number in attributes, per AMP's API.
            const pending = phoneNumber
              ? window.BIS.create(null, variantId, productId, { phone_number: phoneNumber })
              : window.BIS.create(email, variantId, productId);

            if (!pending || typeof pending.then !== 'function') {
              reject(new Error('Something went wrong. Please try again.'));
              return;
            }

            pending.then((data) => {
              if (data && data.status === 'Error') {
                reject(new Error(this.formatBackInStockErrors(data.errors)));
                return;
              }
              resolve(data);
            });
          } catch (error) {
            reject(error);
          }
        });

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timed out. Please try again.')), 10000);
        });

        await Promise.race([request, timeoutPromise]);
        this.showSuccess();

        // Store the address for future pre-fill
        try {
          if (email) localStorage.setItem('bis_customer_email', email);
          if (phoneNumber) localStorage.setItem('bis_customer_phone', phoneNumber);
        } catch (e) {
          // localStorage not available
        }

        // Fire custom event for analytics
        this.dispatchEvent(new CustomEvent('bis-drawer:subscribed', {
          bubbles: true,
          detail: {
            email,
            phoneNumber,
            method: this.notifyMethod,
            variantId: this.selectedVariantId,
            productId: this.productId,
            size: this.selectedSizeValue
          }
        }));

      } catch (error) {
        console.error('[BIS Drawer] Subscription error:', error);
        this.showError(error.message || 'Something went wrong. Please try again.');
      } finally {
        this.isSubmitting = false;
        this.submitButton?.classList.remove('loading');
        this.submitButton?.removeAttribute('disabled');
        this.submitSpinner?.classList.add('hidden');
      }
    }

    // ----- NOTIFICATION METHOD (EMAIL / SMS) -----

    setNotifyMethod(method, { focus = true } = {}) {
      this.notifyMethod = method === 'sms' ? 'sms' : 'email';

      this.methodTabs?.forEach((tab) => {
        const isActive = tab.dataset.bisMethod === this.notifyMethod;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.methodFields?.forEach((field) => {
        const isActive = field.dataset.bisField === this.notifyMethod;
        field.toggleAttribute('hidden', !isActive);

        // Disable rather than just hide: a hidden-but-enabled required field
        // blocks submit with a validation bubble the user can't see.
        const input = field.querySelector('input');
        if (input) {
          input.disabled = !isActive;
          input.required = isActive;
        }
      });

      this.errorMessage?.setAttribute('hidden', '');
      if (focus) {
        const activeInput = this.notifyMethod === 'sms' ? this.phoneInput : this.emailInput;
        activeInput?.focus();
      }
    }

    // AMP requires E.164. Accept the ways people actually type a number and
    // normalise; anything still ambiguous returns null so the caller can ask
    // again rather than sending a number AMP will silently reject.
    // A bare leading 0 is assumed UK — the store's home market.
    normalisePhoneNumber(raw) {
      const digits = (raw || '').replace(/[\s().-]/g, '');
      if (!digits) return null;

      let candidate = digits;
      if (candidate.startsWith('00')) {
        candidate = '+' + candidate.slice(2);
      } else if (candidate.startsWith('0')) {
        candidate = '+44' + candidate.slice(1);
      } else if (!candidate.startsWith('+')) {
        return null;
      }

      return /^\+[1-9]\d{7,14}$/.test(candidate) ? candidate : null;
    }

    // Errors come back as { fieldName: ['message', ...] }. Surface the messages
    // themselves — the field names are AMP's internals and mean nothing here.
    formatBackInStockErrors(errors) {
      const messages = Object.values(errors || {}).flat().filter(Boolean);
      return messages.length ? messages.join(' ') : 'Something went wrong. Please try again.';
    }

    // AMP documents no ready event or callback queue, so poll for the global
    // rather than assume the app embed has run by the time the drawer opens.
    ensureBackInStockLoaded(timeoutMs = 5000) {
      return new Promise((resolve) => {
        if (window.BIS) {
          resolve();
          return;
        }

        const startedAt = Date.now();
        const poll = setInterval(() => {
          if (window.BIS || Date.now() - startedAt >= timeoutMs) {
            clearInterval(poll);
            resolve();
          }
        }, 100);
      });
    }

    // ----- HELPER METHODS -----

    selectAvailableVariant(variantId) {
      // Close drawer
      this.close();

      // Dispatch event for product-info to handle
      this.dispatchEvent(new CustomEvent('bis-drawer:variant-selected', {
        bubbles: true,
        detail: { variantId }
      }));

      // Try to find and click the variant option on the page
      const productInfo = document.querySelector('product-info');
      if (productInfo) {
        // Find the variant in our list
        const variant = this.variants.find(v => v.id === parseInt(variantId));
        if (variant && variant.options) {
          // Try to find and click the size option
          const sizeValue = variant.options[this.sizeOptionPosition - 1];
          if (sizeValue) {
            const sizeInput = productInfo.querySelector(
              `input[type="radio"][value="${sizeValue}"], ` +
              `input[data-option-value-id][value="${sizeValue}"]`
            );
            if (sizeInput) {
              sizeInput.click();
              return;
            }
          }
        }
      }
    }

    prefillCustomerEmail() {
      // Try to get customer email from various sources
      let customerEmail = null;

      // Check Shopify customer
      if (window.ShopifyAnalytics?.meta?.page?.customerId) {
        // Customer is logged in but we don't have email directly
        // Check for email in forms
        const customerEmailInput = document.querySelector('input[name="customer[email]"]');
        if (customerEmailInput?.value) {
          customerEmail = customerEmailInput.value;
        }
      }

      // Check localStorage
      if (!customerEmail) {
        try {
          customerEmail = localStorage.getItem('bis_customer_email');
        } catch (e) {
          // localStorage not available
        }
      }

      if (customerEmail && this.emailInput && !this.emailInput.value) {
        this.emailInput.value = customerEmail;
      }
    }

    validateEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    }
  }

  customElements.define('back-in-stock-drawer', BackInStockDrawer);
}

// Global function to open BIS drawer (for use in onclick handlers)
window.openBackInStockDrawer = function(productId, triggeredBy) {
  // Try to find drawer - first by product ID, then any drawer
  let drawer = document.querySelector(`back-in-stock-drawer[data-product-id="${productId}"]`);

  if (!drawer) {
    drawer = document.querySelector('back-in-stock-drawer');
  }

  if (drawer) {
    drawer.open(triggeredBy);
  } else {
    console.error('[BIS Drawer] No drawer found on page.');
  }
};
